import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { combineLatest, firstValueFrom, map, switchMap, takeUntil } from 'rxjs';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { RoomsService } from '../../../core/services/rooms.service';
import { roomsActions } from '../../../core/store/rooms';
import { selectRoomById } from '../../../core/store/rooms/rooms.reducer';
import { Room } from '../../../core/models/entities/room';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { Vendor } from '../../../core/models/entities/vendor';
import { RoomAmenity } from '../../../core/models/entities/room-amenity';
import { ToastService } from '../../../core/services/toast.service';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { TranslatedPipe } from '../../../core/pipes/translated.pipe';
import { fromStore } from 'angular/app/core/store';
import { CancelConditionsHelper } from 'angular/app/core/helpers/cancel-conditions.helper';
import { RoomDetailsModalComponent } from '../../../bookings/components/room-details-modal/room-details-modal.component';
import { ModalService } from 'angular/app/core/services/modal.service';
import { BookableBy } from 'angular/app/core/models/common/bookable-by';
import { NoParticularLayoutId } from 'angular/app/core/models/entities/layout';
import { ApiError } from '../../../core/models/common/api-error';
import { addDays, differenceInMinutes, format, parseISO } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { sortBy, uniq } from 'lodash';
import { LocaleService } from '../../../core/services/locale.service';
import { TranslocoService } from '@jsverse/transloco';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';
import { translatedValue } from '../../../core/helpers/translated-value.helper';

const ROOM_PAGE_INCLUDES = [
  'amenities',
  'prices',
  'documents',
  'layouts',
  'photos',
  'vendor',
  'building',
  'time_slots',
];

const DATE_FNS_LOCALES = { fr, en: enUS };

const ROOM_DETAILS_MODAL_INCLUDES = [
  'amenities',
  'documents',
  'photos',
  'layouts',
  'building',
];

@Component({
  selector: 'app-room-page',
  animations: [fadeAnimation],
  template: `
    <div class="bg-white">
      @if (loading) {
        <div>
          <div class="container pt-4 pb-10 lg:pb-16">
            <!-- Breadcrumb -->
            <app-skeleton-loader
              width="280px"
              height="16px"
            ></app-skeleton-loader>

            <!-- Détails de la salle : infos à gauche, carousel à droite (lg:grid-cols-5) -->
            <div class="mt-10 grid gap-8 md:mt-16 lg:grid-cols-5 lg:gap-20">
              <!-- Colonne gauche : titre + description + features -->
              <div class="lg:col-span-2">
                <app-skeleton-loader
                  width="70%"
                  height="32px"
                ></app-skeleton-loader>
                <app-skeleton-loader
                  class="mt-3 block"
                  width="40%"
                  height="20px"
                ></app-skeleton-loader>
                <div class="mt-8 space-y-3">
                  <app-skeleton-loader
                    width="100%"
                    height="200px"
                  ></app-skeleton-loader>
                </div>
                <div class="mt-8 flex gap-4">
                  <app-skeleton-loader
                    class="block flex-none"
                    width="140px"
                    height="40px"
                  ></app-skeleton-loader>
                  <app-skeleton-loader
                    class="block flex-none"
                    width="140px"
                    height="40px"
                  ></app-skeleton-loader>
                </div>
              </div>
              <!-- Colonne droite : carousel photo -->
              <app-skeleton-loader
                class="block lg:col-span-3"
                width="100%"
                height="360px"
              ></app-skeleton-loader>
            </div>

            <!-- Section tarifs / conditions d'annulation / équipements -->
            <div class="mt-20 grid gap-8 md:grid-cols-2 md:gap-20">
              <div>
                <app-skeleton-loader
                  width="50%"
                  height="24px"
                ></app-skeleton-loader>
                <div class="mt-6 space-y-2 rounded-lg bg-gray-50 p-4">
                  <div class="flex justify-between gap-4">
                    <app-skeleton-loader
                      width="80px"
                      height="16px"
                    ></app-skeleton-loader>
                    <app-skeleton-loader
                      width="60px"
                      height="16px"
                    ></app-skeleton-loader>
                  </div>
                  <div class="flex justify-between gap-4">
                    <app-skeleton-loader
                      width="60px"
                      height="16px"
                    ></app-skeleton-loader>
                    <app-skeleton-loader
                      width="60px"
                      height="16px"
                    ></app-skeleton-loader>
                  </div>
                </div>
              </div>
              <div>
                <app-skeleton-loader
                  width="50%"
                  height="24px"
                ></app-skeleton-loader>
                <div class="mt-6 space-y-2">
                  <app-skeleton-loader
                    width="100%"
                    height="120px"
                  ></app-skeleton-loader>
                </div>
              </div>
            </div>
          </div>

          <!-- Section dispositions (hors container, comme le vrai contenu) -->
          <div class="md:mt-20">
            <div class="container">
              <app-skeleton-loader
                class="mx-auto block"
                width="280px"
                height="24px"
              ></app-skeleton-loader>
            </div>
            <div class="sm:ml-6 lg:container lg:mx-auto">
              <app-skeleton-loader
                class="mt-6 block"
                width="100%"
                height="200px"
              ></app-skeleton-loader>
            </div>
          </div>

          <!-- Section contacts -->
          <div class="container mt-20">
            <app-skeleton-loader
              class="mx-auto block"
              width="240px"
              height="24px"
            ></app-skeleton-loader>
            <div
              class="mt-10 flex flex-col justify-center gap-10 md:flex-row md:gap-32"
            >
              <app-skeleton-loader
                width="200px"
                height="80px"
              ></app-skeleton-loader>
              <app-skeleton-loader
                width="200px"
                height="80px"
              ></app-skeleton-loader>
            </div>
          </div>
        </div>
      }

      @if (loadingError()) {
        <app-not-found></app-not-found>
      } @else {
        @if (room && !loading) {
          <div>
            <div class="container pt-4 pb-10 lg:pb-16">
              <app-breadcrumb
                [links]="[
                  {
                    title: ('room-page.breadcrumb-search' | transloco),
                    routerLink: vendorLink(room.vendor.slug),
                  },
                  { title: roomName },
                ]"
              ></app-breadcrumb>
              <div class="mt-10 md:mt-16">
                <app-room-details
                  [room]="room"
                  [vendor]="vendor"
                  [bookingButtonVisible]="bookingButtonVisible"
                  [onRoomClick]="openRoomDetailsModal"
                ></app-room-details>
              </div>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <div class="mt-20 grid gap-8 md:grid-cols-2 md:gap-20">
                <div class="space-y-4">
                  <h3>{{ 'room-page.rates.title' | transloco }}</h3>
                  @for (rate of ratesGroupedByPrice; track rate.id) {
                    @if (rate.groups.length > 1) {
                      <div class="space-y-5 rounded-lg bg-gray-50 p-4">
                        @if (ratesGroupedByPrice.length > 1) {
                          <div class="text-lg font-medium">{{ rate | translated:'name' }}</div>
                        }
                        @for (group of rate.groups; track group.days) {
                          <div class="space-y-2">
                            <div class="font-medium">{{ group.days }}</div>
                            <div class="space-y-1">
                              <div class="flex justify-between">
                                <span class="whitespace-nowrap"
                                  >{{ 'room-page.rates.per-day' | transloco }}</span
                                >
                                <span class="text-right whitespace-nowrap">
                                  @if (group.amount_per_day > 0) {
                                    {{ group.amount_per_day | chf }}
                                  } @else {
                                    {{ 'room-page.rates.free' | transloco }}
                                  }
                                </span>
                              </div>
                              @if (room.hourly_rate_enabled) {
                                @if (room.uses_time_slots) {
                                  @for (
                                    time_slot of group.amount_per_time_slot;
                                    track time_slot.duration
                                  ) {
                                    <div class="flex justify-between">
                                      <span>{{ 'room-page.rates.time-slot-prefix' | transloco: { duration: time_slot.duration } }}</span>
                                      <span class="text-right">
                                        @if (time_slot.amount > 0) {
                                          {{ time_slot.amount | chf }}
                                        } @else {
                                          {{ 'room-page.rates.free' | transloco }}
                                        }
                                      </span>
                                    </div>
                                  }
                                } @else {
                                  <div class="flex justify-between">
                                    <span>{{ 'room-page.rates.per-hour' | transloco }}</span>
                                    <span class="text-right">
                                      @if (group.amount_per_hour > 0) {
                                        {{ group.amount_per_hour | chf }}
                                      } @else {
                                        {{ 'room-page.rates.free' | transloco }}
                                      }
                                    </span>
                                  </div>
                                }
                              }
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div
                        class="flex flex-col justify-between gap-2 rounded-lg bg-gray-50 p-4"
                      >
                        @if (ratesGroupedByPrice.length > 1) {
                          <div class="font-medium">{{ rate | translated:'name' }}</div>
                        }
                        <div class="space-y-1">
                          <div class="flex justify-between gap-6">
                            <span class="whitespace-nowrap">{{ 'room-page.rates.per-day' | transloco }}</span>
                            <span class="text-right whitespace-nowrap">
                              @if (rate.groups[0].amount_per_day > 0) {
                                {{ rate.groups[0].amount_per_day | chf }}
                              } @else {
                                {{ 'room-page.rates.free' | transloco }}
                              }
                            </span>
                          </div>
                          @if (room.hourly_rate_enabled) {
                            @if (room.uses_time_slots) {
                              @for (
                                time_slot of rate.groups[0]
                                  .amount_per_time_slot;
                                track time_slot.duration
                              ) {
                                <div class="flex justify-between">
                                  <span>{{ 'room-page.rates.time-slot-prefix' | transloco: { duration: time_slot.duration } }}</span>
                                  <span class="text-right">
                                    @if (time_slot.amount > 0) {
                                      {{ time_slot.amount | chf }}
                                    } @else {
                                      {{ 'room-page.rates.free' | transloco }}
                                    }
                                  </span>
                                </div>
                              }
                            } @else {
                              <div class="flex justify-between">
                                <span>{{ 'room-page.rates.per-hour' | transloco }}</span>
                                <span class="text-right">
                                  @if (rate.groups[0].amount_per_hour) {
                                    {{ rate.groups[0].amount_per_hour | chf }}
                                  } @else {
                                    {{ 'room-page.rates.free' | transloco }}
                                  }
                                </span>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  }
                </div>
                <div>
                  <h3>{{ 'room-page.cancel-conditions.title' | transloco }}</h3>
                  <div class="pt-8">
                    @for (
                      summaryLine of cancelConditionsSummaryLines;
                      track summaryLine
                    ) {
                      <div class="mb-1">{{ summaryLine }} </div>
                    }
                  </div>
                  <div class="mt-4 inline-block">
                    @if (vendor.grace_period_hours) {
                      <app-alert>
                        <div class="xl:hidden">
                          {{ 'cancel-conditions.grace-period-intro' | transloco }}
                          <span class="font-medium"
                            >{{ 'cancel-conditions.grace-period-detail' | transloco: { hours: vendor.grace_period_hours } }}</span
                          >.
                        </div>
                        <div class="hidden xl:block">
                          <div>{{ 'cancel-conditions.grace-period-intro' | transloco }}</div>
                          <div class="font-medium"
                            >{{ 'cancel-conditions.grace-period-detail' | transloco: { hours: vendor.grace_period_hours } }}.
                          </div>
                        </div>
                      </app-alert>
                    }
                  </div>
                </div>
                @if (room.room_amenities?.length) {
                  <div>
                    <h3>{{ 'room-page.amenities.title' | transloco }}</h3>
                    <div class="pt-2">
                      <app-room-view-amenities
                        [roomAmenities]="room.room_amenities"
                      ></app-room-view-amenities>
                    </div>
                  </div>
                }
                @if (room.documents?.length) {
                  <div>
                    <h3>{{ 'room-page.documents.title' | transloco }}</h3>
                    <app-room-view-documents
                      [documents]="room.documents"
                    ></app-room-view-documents>
                  </div>
                }
              </div>
            </div>
            <!-- ------------------------------------------------------------------------------>
            <!-- ------------------------------------------------------------------------------>
            <!-- ------------------------------------------------------------------------------>
            <div class="md:mt-20">
              <div class="container">
                <h3 class="md:text-center">{{ 'room-page.layouts.title' | transloco }}</h3>
                @if (!noParticularLayout) {
                  <div class="mt-4 md:mt-6 md:text-center">{{ 'room-page.layouts.no-particular-layout-description' | transloco }}</div>
                }
              </div>
              <div class="sm:ml-6 lg:container lg:mx-auto">
                <app-room-view-layouts
                  [layouts]="room.layouts"
                ></app-room-view-layouts>
              </div>
            </div>
            <div class="container md:mt-32">
              <div class="bg-secondary-900 mt-10 h-px"></div>
              <div class="pt-10 md:pt-32">
                <h3 class="md:text-center">{{ 'room-page.contacts.title' | transloco }}</h3>
                <div
                  class="mt-8 flex flex-col justify-center space-y-10 md:flex-row md:space-y-0 md:space-x-32"
                >
                  <div>
                    <app-room-view-contact
                      [room]="room"
                      type="admin"
                    ></app-room-view-contact>
                  </div>
                  @if (room.technical_email || room.technical_phone) {
                    <div>
                      <app-room-view-contact
                        [room]="room"
                        type="technical"
                      ></app-room-view-contact>
                    </div>
                  }
                </div>
              </div>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              @if (bookingButtonVisible) {
                <div class="mt-14 text-center md:mt-20">
                  <app-button [routerLink]="bookingLink()">{{ 'room-page.book-button' | transloco }}</app-button>
                </div>
              }
              @if (!bookingButtonVisible && !room.is_bookable_publicly) {
                <div class="mt-14 md:mt-20">
                  <app-info-box variant="info">
                    {{ notBookablePubliclyMessage }}
                  </app-info-box>
                </div>
              }
            </div>
          </div>
        }
      }
    </div>

    <div class="h-20 bg-white"></div>
  `,
  standalone: false,
})
export class RoomPageComponent extends DestroyableComponent implements OnInit {
  room: Room;
  includedAmenities: RoomAmenity[] = [];
  excludedAmenities: RoomAmenity[] = [];
  vendor: Vendor;
  loading: boolean;
  bookingButtonVisible: boolean;
  ratesGroupedByPrice: {
    id: number;
    name: string;
    groups: {
      days: string;
      amount_per_day: number;
      amount_per_hour?: number;
      amount_per_time_slot?: { duration: string; amount: number }[];
    }[];
  }[];
  loadingError = signal<boolean>(false);

  private translocoService = inject(TranslocoService);
  private localizedUrlService = inject(LocalizedUrlService);

  // Lien localisé vers la page du vendor
  vendorLink(vendorSlug: string): string[] {
    return this.localizedUrlService.link('vendor', { vendorSlug });
  }

  // Lien localisé vers la réservation de la salle courante
  bookingLink(): string[] {
    return this.localizedUrlService.link('booking', {
      vendorSlug: this.room.vendor.slug,
      id: this.room.id,
    });
  }

  constructor(
    private route: ActivatedRoute,
    private roomsService: RoomsService,
    private toastService: ToastService,
    private title: Title,
    private store: Store,
    private modalService: ModalService,
    private localeService: LocaleService,
    private translatedPipe: TranslatedPipe,
  ) {
    super();
  }

  private authUser = this.store.selectSignal(fromStore.selectAuthUser);

  ngOnInit(): void {
    // Les tarifs et le titre sont précalculés depuis la map translations et via
    // translocoService.translate(). On les recalcule après chaque changement de
    // langue (langChanges$ émet une fois le fichier de langue chargé et activé,
    // donc translate() et la locale active sont cohérents).
    this.translocoService.langChanges$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.room && !this.loading) {
          this.setRatesGroupedByPrice();
          this.updateTitle();
        }
      });

    this.route.params
      .pipe(
        takeUntil(this.onDestroy$),
        switchMap((params: Params) => {
          this.loading = true;
          this.loadingError.set(false);
          const id = +params['id'];
          this.store.dispatch(
            roomsActions.needRoom({ id, includes: ROOM_PAGE_INCLUDES }),
          );
          return combineLatest([
            this.store.select(selectRoomById(id)),
            this.store.select(fromStore.selectRoomsError),
          ]).pipe(
            map(([room, error]) => ({
              room,
              error,
              vendorSlug: params['vendorSlug'],
            })),
          );
        }),
      )
      .subscribe(({ room, error, vendorSlug }) => {
        if (room) {
          this.displayRoom(room, vendorSlug);
        } else if (error) {
          this.loadingError.set(true);
          this.loading = false;
        }
      });
  }

  private displayRoom(room: Room, vendorSlug: string): void {
    this.room = room;
    if (
      room.vendor.slug !== vendorSlug ||
      room.bookable_by === BookableBy.Private ||
      !this.roomsService.canUserAccessRoom(room)
    ) {
      this.loadingError.set(true);
      this.loading = false;
      return;
    }
    this.includedAmenities = room.room_amenities.filter(
      (roomAmenity: RoomAmenity) => roomAmenity.included,
    );
    this.excludedAmenities = room.room_amenities.filter(
      (roomAmenity: RoomAmenity) => !roomAmenity.included,
    );
    this.vendor = room.vendor;
    const user = this.authUser();
    const isInternalUser = !!user && user.vendor.id === room.vendor_id;
    this.bookingButtonVisible =
      isInternalUser ||
      (this.vendor.accepts_online_payments && room.is_bookable_publicly);
    this.setRatesGroupedByPrice();
    this.loading = false;
    this.updateTitle();
  }

  get roomName(): string {
    const locale = this.localeService.locale();
    return translatedValue(this.room, 'name', locale) as string;
  }

  private updateTitle(): void {
    this.title.setTitle(
      this.translocoService.translate('room-page.page-title', {
        room: this.roomName,
        vendor: this.vendor.name,
      }),
    );
  }

  get cancelConditionsSummaryLines() {
    return CancelConditionsHelper.getCancelConditionLines(
      this.room.vendor.cancel_conditions,
      this.translocoService,
    );
  }

  get noParticularLayout(): boolean {
    return (
      this.room.layouts.length === 1 &&
      this.room.layouts[0].id === NoParticularLayoutId
    );
  }

  setRatesGroupedByPrice() {
    const timeSlotsDurationsInMinutes =
      this.getRoomTimeSlotsDurationInMinutes();

    this.ratesGroupedByPrice = this.room.current_prices.reduce(
      (result, price) => {
        // Vérifie si un groupe de tarif similaire existe déjà
        const rateIndex = result.findIndex((rate) => rate.id === price.rate.id);

        if (rateIndex === -1) {
          // Ajoute un nouveau tarif
          result.push({
            id: price.rate.id,
            name: this.translatedPipe.transform(price.rate, 'name') as string,
            groups: [],
          });
        }

        // Ajoute un groupe à ce tarif
        const rate = result.find((r) => r.id === price.rate.id);
        const groupIndex = rate.groups.findIndex(
          (group) =>
            group.amount_per_day === price.amount_per_day &&
            group.amount_per_hour === price.amount_per_hour,
        );

        const allDays = this.translocoService.translate('room-page.rates.all-days');
        const dayDescription = this.room.weekday_pricing_enabled
          ? this.getWeekdayDescription(price.weekday, allDays)
          : allDays;

        if (groupIndex === -1) {
          // Ajoute un nouveau groupe
          rate.groups.push({
            days: dayDescription,
            amount_per_day: price.amount_per_day,
            amount_per_hour: price.amount_per_hour,
            amount_per_time_slot: !this.room.uses_time_slots
              ? null
              : timeSlotsDurationsInMinutes.map((minutes: number) => {
                  return {
                    duration: this.formatHours(minutes),
                    amount: (minutes / 60) * price.amount_per_hour,
                  };
                }),
          });
        } else {
          // Ajoute le jour au groupe existant
          rate.groups[groupIndex].days += this.room.weekday_pricing_enabled
            ? ', ' + dayDescription.toLowerCase()
            : '';
        }

        return result;
      },
      [],
    );
  }

  // Retourne le nom du jour localisé via date-fns (lundi..dimanche), première
  // lettre en majuscule. Le weekday -1 (jours fériés) n'a pas d'équivalent
  // date-fns : on retombe sur une clé de traduction dédiée.
  private getWeekdayDescription(weekday: number, fallback: string): string {
    if (weekday === -1) {
      return this.translocoService.translate('weekdays.holidays');
    }
    if (weekday == null || weekday < 0 || weekday > 6) {
      return fallback;
    }
    const locale = DATE_FNS_LOCALES[this.localeService.locale()] ?? fr;
    // 2023-01-01 est un dimanche (weekday 0) ; on décale du nombre de jours.
    const date = addDays(new Date(2023, 0, 1), weekday);
    const name = format(date, 'EEEE', { locale });
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  private formatHours(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins < 60 && hours === 0) {
      return `${mins}min`;
    } else {
      return mins === 0 ? `${hours}h` : `${hours}h${mins}`;
    }
  }

  private getRoomTimeSlotsDurationInMinutes() {
    const timeSlotsDurationsInMinutes: number[] = [];
    if (this.room.time_slots) {
      this.room.time_slots.forEach((timeSlot) => {
        timeSlotsDurationsInMinutes.push(
          differenceInMinutes(parseISO(timeSlot.end), parseISO(timeSlot.start)),
        );
      });
    }

    return sortBy(uniq(timeSlotsDurationsInMinutes));
  }

  get notBookablePubliclyMessage(): string {
    const locale = this.localeService.locale();
    const message = translatedValue(
      this.room,
      'is_bookable_publicly_message',
      locale,
    ) as string;
    return (
      message ||
      this.translocoService.translate('room-page.not-bookable-publicly-fallback')
    );
  }

  identify(index, item) {
    return item.id;
  }

  openRoomDetailsModal = async (room: Room) => {
    try {
      const response = await firstValueFrom(
        this.roomsService.findOne(room.id, ROOM_DETAILS_MODAL_INCLUDES),
      );
      const fullRoom = response.data;
      const modalRef = this.modalService.open(RoomDetailsModalComponent, {
        size: 'screen',
        showActionButtons: false,
        title: fullRoom.name,
      });
      const component = modalRef.componentRef
        .instance as RoomDetailsModalComponent;
      component.room = fullRoom;
      component.vendor = this.vendor;
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    }
  };
}
